import { api, getAccessTokenForFetch } from '../../api'
import type {
  KrakenGraphResponse,
  KrakenChatRequest,
  KrakenChatResponse,
  KrakenStreamEvent,
  KrakenExpandEvent,
  ClusterDetailParams,
  ClusterListParams,
  CreateClusterPayload,
  ExpandClusterOptions,
  O2OGraph,
  O2OGraphDetail,
  O2OGraphListResponse,
  UpdateClusterPayload,
} from '../../../types/kraken.types'

export const getNetworkFromVertex = async (
  vertexId: string,
): Promise<KrakenGraphResponse> => {
  const res = await api.get(`/kraken/network/${vertexId}/`)
  return {
    root: res.data.nodes[0]?.id,
    nodes: res.data.nodes,
    node_count: res.data.nodes.length,
    edge_count: 0, 
    edges: [],
  }
}

/**
 * Chat with Octopus AI (non-streaming fallback)
 * POST /kraken/chat/
 */
export const chatWithKraken = async (data: KrakenChatRequest): Promise<KrakenChatResponse> => {
  const res = await api.post('/kraken/chat/', data)
  return res.data
}

/**
 * Chat with Octopus AI via SSE streaming
 * POST /kraken/chat/stream/
 */
export const chatWithKrakenStream = async (
  data: KrakenChatRequest,
  onEvent: (event: KrakenStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  const response = await fetch(`${baseURL}/kraken/chat/stream/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
    signal,
  })

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Parse SSE lines from buffer
    const lines = buffer.split('\n')
    buffer = lines.pop() || '' // Keep incomplete line in buffer

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) {
        try {
          const event: KrakenStreamEvent = JSON.parse(trimmed.slice(6))
          onEvent(event)
        } catch {
          // Skip malformed events
        }
      }
    }
  }

  // Process any remaining buffer
  if (buffer.trim().startsWith('data: ')) {
    try {
      const event: KrakenStreamEvent = JSON.parse(buffer.trim().slice(6))
      onEvent(event)
    } catch {
      // Skip
    }
  }
}

/**
 * Streaming node expansion: Neptune connections + Firecrawl web discovery
 * GET /kraken/expand/<vertex_id>/
 */
export const expandNodeStream = async (
  vertexId: string,
  onEvent: (event: KrakenExpandEvent) => void,
): Promise<void> => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  const response = await fetch(`${baseURL}/kraken/expand/${vertexId}/`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    throw new Error(`Expand request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) {
        try {
          const event: KrakenExpandEvent = JSON.parse(trimmed.slice(6))
          onEvent(event)
        } catch {
          // Skip malformed events
        }
      }
    }
  }

  if (buffer.trim().startsWith('data: ')) {
    try {
      const event: KrakenExpandEvent = JSON.parse(buffer.trim().slice(6))
      onEvent(event)
    } catch {
      // Skip
    }
  }
}

// ─── O2O Graphs (KrakenO2O) ───────────────────────────────────────────────────

export const getClusterList = async (params?: ClusterListParams): Promise<O2OGraphListResponse> => {
  const res = await api.get<O2OGraphListResponse>('/o2o/graphs/', { params })
  return res.data
}

export const getClusterDetail = async (id: string, params?: ClusterDetailParams): Promise<O2OGraphDetail> => {
  const res = await api.get<O2OGraphDetail>(`/o2o/graphs/${id}/`, { params })
  return res.data
}

export const postCluster = async (data: CreateClusterPayload): Promise<O2OGraph> => {
  const res = await api.post<O2OGraph>('/o2o/graphs/', data)
  return res.data
}

export const patchCluster = async (id: string, data: UpdateClusterPayload): Promise<O2OGraph> => {
  const res = await api.patch<O2OGraph>(`/o2o/graphs/${id}/`, data)
  return res.data
}

export const deleteCluster = async (id: string): Promise<void> => {
  await api.delete(`/o2o/graphs/${id}/`)
}

export const expandCluster = async (id: string, options?: ExpandClusterOptions): Promise<void> => {
  const params = options?.expand ? { expand: options.expand } : {}
  const body = {
    ...(options?.expand_all && { expand_all: options.expand_all }),
    ...(options?.node_id && { node_id: options.node_id }),
  }
  await api.post(`/o2o/graphs/${id}/expand/`, body, { params })
}

export const getClusterExpansionStatus = async (id: string, params?: ClusterDetailParams): Promise<O2OGraphDetail> => {
  const res = await api.get<O2OGraphDetail>(`/o2o/graphs/${id}/expand/`, { params })
  return res.data
}

export const expandNode = async (id: string, nodeId: string): Promise<void> => {
  await api.post(`/o2o/graphs/${id}/expand_node/`, { node_id: nodeId })
}
